import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CalendarClock, ClipboardCheck, MessageSquare, AlertTriangle, Users,
  ArrowRight, LifeBuoy, Target,
} from "lucide-react";

const HELP_LABELS = {
  substance_or_gambling_urge: "Vontade de beber, usar ou jogar",
  anxiety: "Ansiedade",
  counselor_request: "Falar com o conselheiro",
  support_network_request: "Rede de apoio",
  emergency: "Atendimento de urgência",
};

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <Card className="rounded-2xl p-4 border-[#E0F2FE] card-hover">
      <div className={`w-10 h-10 rounded-xl grid place-items-center mb-2 ${tone}`}><Icon size={18} /></div>
      <div className="text-2xl font-bold text-[#1F3B4D] font-heading">{value}</div>
      <div className="text-xs text-[#94A3B8]">{label}</div>
    </Card>
  );
}

export default function ProDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/professional/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return null;

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-up">
      <div>
        <p className="text-sm text-[#94A3B8]">Bom trabalho, {user?.name?.split(" ")[0]}.</p>
        <h1 className="font-heading text-3xl font-extrabold text-[#1F3B4D]">O que preciso considerar hoje?</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat icon={Users} label="Pacientes" value={d.total_patients} tone="bg-[#0F766E]/10 text-[#0F766E]" />
        <Stat icon={CalendarClock} label="Sessões hoje" value={d.today_sessions.length} tone="bg-[#60A5FA]/15 text-[#2563eb]" />
        <Stat icon={ClipboardCheck} label="Para revisar" value={d.pending_reviews.length} tone="bg-[#34D399]/15 text-[#0F766E]" />
        <Stat icon={MessageSquare} label="Mensagens" value={d.unread_messages.reduce((a, m) => a + m.count, 0)} tone="bg-[#F59E0B]/15 text-[#b26a00]" />
        <Stat icon={AlertTriangle} label="Pedidos de ajuda" value={(d.help_requests || []).length} tone="bg-[#EF4444]/15 text-[#dc2626]" />
      </div>

      {(d.help_requests || []).length > 0 && (
        <Card className="rounded-2xl p-5 border-0" data-testid="help-requests-banner"
          style={{ background: "linear-gradient(90deg,rgba(239,68,68,.12),rgba(245,158,11,.10))" }}>
          <div className="flex items-center gap-2 text-[#dc2626] font-semibold mb-3"><LifeBuoy size={18} /> Pedidos de ajuda</div>
          <div className="space-y-2">
            {d.help_requests.map((s) => (
              <div key={s.id} data-testid={`help-request-${s.id}`} className="flex items-center justify-between bg-white rounded-xl p-3 border border-[#EF4444]/20">
                <div>
                  <div className="font-semibold text-[#1F3B4D] text-sm">{s.patient_name}</div>
                  <div className="text-xs text-[#94A3B8]">{HELP_LABELS[s.trigger_context] || s.trigger_context}{s.message ? ` — ${s.message}` : ""}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#EF4444]/10 text-[#dc2626] border-0 text-[11px] capitalize">{s.status.replace("_", " ")}</Badge>
                  <Button size="sm" variant="outline" className="rounded-full border-[#E0F2FE]" onClick={() => nav(`/pacientes/${s.patient_id}`)}>Abrir</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <section>
          <h3 className="font-heading font-bold text-[#1F3B4D] mb-3 flex items-center gap-2"><CalendarClock size={18} className="text-[#0F766E]" /> Sessões de hoje</h3>
          <div className="space-y-2">
            {d.today_sessions.map((s) => (
              <Card key={s.id} className="rounded-2xl p-4 border-[#E0F2FE] card-hover flex items-center justify-between" data-testid={`today-session-${s.id}`}>
                <div>
                  <div className="font-semibold text-[#1F3B4D]">{s.patient_name}</div>
                  <div className="text-xs text-[#94A3B8]">{new Date(s.scheduled_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} · {s.location || "—"}</div>
                </div>
                <Button size="sm" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-1" onClick={() => nav(`/pacientes/${s.patient_id}`)}>Preparar <ArrowRight size={14} /></Button>
              </Card>
            ))}
            {d.today_sessions.length === 0 && <p className="text-sm text-[#94A3B8]">Nenhuma sessão para hoje.</p>}
          </div>
        </section>

        <section>
          <h3 className="font-heading font-bold text-[#1F3B4D] mb-3 flex items-center gap-2"><ClipboardCheck size={18} className="text-[#0F766E]" /> Atividades para revisão</h3>
          <div className="space-y-2">
            {d.pending_reviews.map((a) => (
              <Card key={a.id} className="rounded-2xl p-4 border-[#E0F2FE] card-hover flex items-center justify-between" data-testid={`review-item-${a.id}`}>
                <div>
                  <div className="font-semibold text-[#1F3B4D]">{a.title}</div>
                  <div className="text-xs text-[#94A3B8]">{a.patient_name}</div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full border-[#E0F2FE]" onClick={() => nav(`/pacientes/${a.patient_id}`)}>Revisar</Button>
              </Card>
            ))}
            {d.pending_reviews.length === 0 && <p className="text-sm text-[#94A3B8]">Nada pendente de revisão.</p>}
          </div>
        </section>
      </div>

      {d.patients_without_action.length > 0 && (
        <Card className="rounded-2xl p-5 border-[#E0F2FE]">
          <div className="flex items-center gap-2 text-[#1F3B4D] font-semibold mb-3"><Target size={18} className="text-[#F59E0B]" /> Pacientes sem próxima ação definida</div>
          <div className="flex flex-wrap gap-2">
            {d.patients_without_action.map((p) => (
              <Badge key={p.id} variant="secondary" className="cursor-pointer hover:bg-[#E0F2FE]" onClick={() => nav(`/pacientes/${p.id}`)} data-testid={`no-action-${p.id}`}>{p.name}</Badge>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
